import { useMemo } from 'react';

import {
  InventoryEndpoints,
  InventoryProvider,
  InventoryRecord,
  InventoryRepo,
  InventoryRepoPlus,
  InventoryTree,
  InventoryType,
  PenInventory,
  SensorInventory,
} from '@scaleaq/sitecontrol-lib';
import { useAuth } from 'react-oidc-context';

import { API_NAMES, CACHE_KEYS } from '@/constants';
import {
  useAuthApiGetSingleFetch,
  useAuthApiGetSingleFetchById,
  useAuthApiPostSingleFetch,
  useAuthApiUpdate,
  useAuthApiUpdateById,
} from '@/dataAccess/rest/apiHooks';
import { useConfigStore } from '@/stores';

import { buildScopedRoute, selectGlobalInventorySites } from './inventory.functions.ts';

const baseApiRoute = API_NAMES.cloudInventoryApi;

/**
 * Flat inventory nodes for one site (`GET /api/v1/inventory/list`). Portable mode scopes with
 * `?inventoryId=`. `InventoryDataProvider` assembles the tree from this list.
 */
export const useGetAllInventory = (inventoryId?: string) => {
  const scope = useInventoryScope(inventoryId);

  return useAuthApiGetSingleFetch<InventoryRepo[]>({
    baseApiRoute,
    route: buildScopedRoute(InventoryEndpoints.getAll, scope.inventoryId),
    cacheKeys: [CACHE_KEYS.inventory],
    enabled: scope.enabled,
  });
};

/**
 * Get inventory by type
 * @param inventoryType
 * @param inventoryId
 */
export const useGetInventoriesByType = ({
  inventoryType,
  inventoryId,
}: {
  inventoryType: InventoryType;
  inventoryId?: string;
}) => {
  const scope = useInventoryScope(inventoryId);
  const route = buildScopedRoute(
    InventoryEndpoints.getByType.replace('{type}', inventoryType),
    scope.inventoryId
  );

  return useAuthApiGetSingleFetch<InventoryTree[]>({
    baseApiRoute,
    route,
    cacheKeys: [CACHE_KEYS.inventory, inventoryType],
    enabled: scope.enabled,
  });
};

/**
 * Get pen inventory
 */
export const useGetPenInventory = (inventoryId?: string) => {
  const scope = useInventoryScope(inventoryId);

  return useAuthApiGetSingleFetch<PenInventory[]>({
    baseApiRoute,
    route: buildScopedRoute(InventoryEndpoints.getAllPens, scope.inventoryId),
    cacheKeys: [CACHE_KEYS.inventory, 'pens', inventoryId],
    enabled: scope.enabled,
  });
};

/**
 * Accessible sites from `GET /api/v1/type/Site`, unscoped (no `inventoryId`).
 * On edge a row without `SiteBase` gets the cluster's own host; in portable mode it is dropped.
 * Scale-internal routing is not taken from this row.
 * When this runs on edge it does not wait for login. In portable mode it waits for auth,
 * because the list is per user. `AccessibleSitesProvider` always runs it: edge needs the
 * inventory site id, and portable mode needs the accessible-site list.
 */
export const useGetAllInventorySites = (enabled = true) => {
  const { user, isAuthenticated } = useAuth();
  const portableMode = useConfigStore(s => s.portableMode);
  // Only edge fills a missing `SiteBase` with its own host; in portable mode such a row is dropped.
  const localBaseUrl = useConfigStore(s => (s.portableMode ? undefined : s.defaultSite?.baseUrl));
  const query = useAuthApiGetSingleFetch<InventoryTree[]>({
    baseApiRoute,
    route: InventoryEndpoints.getByType.replace('{type}', InventoryType.Site),
    cacheKeys: [CACHE_KEYS.cloudInventorySites, InventoryType.Site, user?.profile.sub],
    refetchOnWindowFocus: false,
    // Default staleTime is 0, so cached data is stale immediately and a remount (dev StrictMode,
    // `/config` gate, auth) would refetch the full site table. Five minutes covers a session
    // without freezing a newly provisioned site until a hard reload (`Infinity` would).
    staleTime: 300_000,
    // Edge waits for this list before it renders, and falls back to its own site when the request
    // fails. Retrying, or retrying a 429 without end, would only keep the spinner up.
    retry: portableMode ? undefined : false,
    enabled: enabled && (!portableMode || isAuthenticated),
  });
  const data = useMemo(
    () => selectGlobalInventorySites({ nodes: query.data, localBaseUrl }),
    [localBaseUrl, query.data]
  );
  return { ...query, data };
};

/**
 * Get pen inventory by id
 */
export const useGetPenInventoryById = (inventoryId: string) => {
  return useAuthApiGetSingleFetch<PenInventory>({
    baseApiRoute,
    route: InventoryEndpoints.getPen.replace('{inventoryId}', inventoryId),
    cacheKeys: [CACHE_KEYS.inventory, 'pen', inventoryId],
    enabled: Boolean(inventoryId),
  });
};

/**
 * Get inventories sensors
 * @param inventoryId
 */
export const useGetInventoriesSensors = (inventoryId?: string) => {
  const scope = useInventoryScope(inventoryId);
  const queryKeys = useMemo<string[]>(
    () => [CACHE_KEYS.inventorySensors, `${inventoryId ? inventoryId : ''}`],
    [inventoryId]
  );

  return useAuthApiGetSingleFetch<InventoryTree[]>({
    baseApiRoute,
    route: buildScopedRoute(InventoryEndpoints.getSensors, scope.inventoryId),
    cacheKeys: queryKeys,
    enabled: scope.enabled,
  });
};

/**
 * Fetches sensors and all their ancestors. Optionally filtered by a provider.
 * @param provider - An optional InventoryProvider to filter by
 * @param inventoryId
 * @returns A QueryResult containing a flat list of all sensors and ancestors, or undefined while loading.
 */
export const useGetSensorsAndAncestors = ({
  provider,
  inventoryId,
}: { provider?: InventoryProvider; inventoryId?: string } = {}) => {
  const scope = useInventoryScope(inventoryId);
  const cacheKeys = useMemo(
    () => [CACHE_KEYS.inventory, 'sensorAncestors', ...(provider ? [provider] : [])],
    [provider]
  );
  const route = buildScopedRoute(
    InventoryEndpoints.getSensorsAndAncestors,
    scope.inventoryId,
    provider ? { provider } : undefined
  );

  return useAuthApiGetSingleFetch<SensorInventory[]>({
    baseApiRoute,
    route,
    cacheKeys,
    enabled: scope.enabled,
  });
};

/** Distinct non-deleted inventory types configured on this site (`GET /api/v1/distincttypes`). */
export const useGetDistinctInventoryTypes = (inventoryId?: string) => {
  const scope = useInventoryScope(inventoryId);

  return useAuthApiGetSingleFetch<InventoryType[]>({
    baseApiRoute,
    route: buildScopedRoute(InventoryEndpoints.getDistinctTypes, scope.inventoryId),
    cacheKeys: [CACHE_KEYS.inventory, CACHE_KEYS.inventoryDistinctTypes, scope.inventoryId],
    enabled: scope.enabled,
    refetchOnWindowFocus: false,
  });
};

/**
 * Get a single inventory item by inventory id. Skips the request when `inventoryId` is missing so
 * callers can pass an unresolved parent or device id without hitting 404.
 * The route template stays `{id}` until sitecontrol-lib renames it. See docs/inventory-model.md.
 */
export const useInventory = (inventoryId: string | undefined) => {
  return useAuthApiGetSingleFetch<InventoryTree>({
    baseApiRoute,
    route: InventoryEndpoints.get.replace('{id}', inventoryId ?? ''),
    cacheKeys: [CACHE_KEYS.inventory, inventoryId ?? ''],
    enabled: Boolean(inventoryId),
    refetchOnWindowFocus: false,
  });
};

export const useGetAllCameras = (enabled = true) => {
  return useAuthApiGetSingleFetch<InventoryRepoPlus[]>({
    baseApiRoute,
    route: InventoryEndpoints.getAllCameras,
    cacheKeys: [CACHE_KEYS.inventory, 'cameras'],
    enabled,
  });
};

/**
 * Distinct active inventory types per site, across every accessible site
 * (cloud `GET /api/v1/distincttypesbysite`). Site-grouped counterpart of
 * {@link useGetDistinctInventoryTypes}: answers "which sites carry which device types?" without
 * transferring the inventory items themselves. Sites with no inventory come back with an empty list,
 * so an absent key means no access rather than no devices.
 */
export const useGetDistinctInventoryTypesBySite = (enabled = true) =>
  useAuthApiGetSingleFetch<Record<string, InventoryType[]>>({
    baseApiRoute,
    route: InventoryEndpoints.getDistinctTypesBySite,
    cacheKeys: [CACHE_KEYS.inventory, CACHE_KEYS.inventoryDistinctTypes, 'bySite'],
    enabled,
    refetchOnWindowFocus: false,
  });

/**
 * Fetches an `id → display name` map for every non-deleted inventory item, via the `getNames`
 * endpoint that exists on both APIs (`GET /api/v1/names`). Keyed on the cloud API, so it resolves
 * cross-site against the cloud in portable mode and falls back to the local-site edge API on edge
 * clusters — answering "what is the name of the inventory with this id?" in both deployments.
 */
export const useGetInventoryNames = () => {
  return useAuthApiGetSingleFetch<Record<string, string>>({
    baseApiRoute,
    route: InventoryEndpoints.getNames,
    cacheKeys: [CACHE_KEYS.inventory, 'names'],
  });
};

/**
 * Fetches inventory items of the given types across all accessible sites, each carrying `siteInfo`
 * (cloud `POST /api/v1/type/query`). Used to build cross-site, site-grouped pickers.
 */
export const useGetInventoriesByTypeCrossSite = (types: readonly InventoryType[] | undefined) => {
  const typesKey = types?.length ? types.join(',') : '';

  return useAuthApiPostSingleFetch<InventoryRepoPlus[], { types: readonly InventoryType[] }>({
    baseApiRoute,
    route: InventoryEndpoints.getInventoriesByTypes,
    body: { types: types ?? [] },
    cacheKeys: [CACHE_KEYS.inventory, 'cloudByType', typesKey],
    enabled: typesKey.length > 0,
    returnJsonResult: true,
    refetchOnWindowFocus: false,
  });
};

/** Set whether the site sends sensor data to ScaleAQ Cloud Services. */
export const useUpdateSendSensorDataToCloudServices = (siteInventoryId: string) =>
  useAuthApiUpdate<{ enabled: boolean }>({
    baseApiRoute,
    route: `/api/v1/inventory/site/${siteInventoryId}/sendsensordatatocloudservices`,
    putOrPatch: 'PUT',
    cacheKeys: [CACHE_KEYS.inventory, siteInventoryId],
  });

/** Updates an inventory item (`PUT /api/v1/inventory`). */
export const useUpdateInventory = () => {
  return useAuthApiUpdateById<InventoryTree>({
    baseApiRoute,
    cacheKeys: [CACHE_KEYS.inventory],
    putOrPatch: 'PUT',
    route: InventoryEndpoints.update,
  });
};

/** Moves an item to a new parent (`PUT /api/v1/inventory/{id}/move`). */
export const useMoveInventoryById = () => {
  return useAuthApiUpdateById<InventoryTree>({
    baseApiRoute,
    cacheKeys: [CACHE_KEYS.inventory],
    putOrPatch: 'PUT',
    route: InventoryEndpoints.move,
  });
};

/** Unlinks an inventory item from its parent, thus creating an orphan. */
export const useUnlinkInventoryById = () => {
  return useAuthApiUpdateById<InventoryTree>({
    baseApiRoute,
    cacheKeys: [CACHE_KEYS.inventory],
    putOrPatch: 'PUT',
    route: InventoryEndpoints.unlink,
  });
};

/**
 * History for one inventory node. The query parameter stays `inventoryId`.
 * See docs/inventory-model.md.
 */
export const useGetInventoryHistory = (inventoryId: string) => {
  return useAuthApiGetSingleFetchById<InventoryRecord[]>({
    rawId: inventoryId,
    baseApiRoute,
    route: `${InventoryEndpoints.getHistory}?inventoryId={inventoryId}`,
    cacheKeys: [CACHE_KEYS.inventoryHistory, inventoryId],
  });
};

/**
 * Cloud inventory endpoints require a site inventory id, so wait until one resolves rather than
 * issuing an unscoped cross-site request. Their edge counterparts already represent exactly one
 * site; the generated edge `SiteConfig.id` is only a client-side identity and must not be sent as
 * an inventory filter.
 */
const useInventoryScope = (inventoryId: string | undefined) => {
  const portableMode = useConfigStore(state => state.portableMode);
  return {
    inventoryId: portableMode ? inventoryId : undefined,
    enabled: !portableMode || Boolean(inventoryId),
  };
};
